import React, { useEffect, useState, useRef } from 'react';
import { useGetKnnDataQuery, useGetTempUrlsQuery, useSaveChosenCasesMutation } from '@/redux/slices/stack-and-ship/stack-and-ship-apiSlice';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import SpiderChart, { SpiderChartRef } from './spider-chart';
// import { toast } from '@/components/ui/use-toast';
import { showSuccessToast, showErrorToast } from '../toast/toast';

import { useStepperContext } from '@/pages/distribution-analytics/components/stepper-provider'
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { OptionDetailsModal } from './option-details-modal';
import { Button } from "@/components/ui/button";
import { Input } from '../ui/input';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card } from "@/components/ui/card";
import {
    Panel,
    PanelGroup,
    PanelResizeHandle
} from 'react-resizable-panels';
import { backendApiUrl } from '@/lib/config';
import oktaService from '@/services/OktaService';
import { mockKnnData } from '@/mocks/distributionAnalyticsDemo';
import {
    formatKnnMeasurement,
    getKnnRowSourceType,
    getKnnValue,
    OVERALL_DECISION_ALIASES,
    UNITLOAD_SIMILARITY_ALIASES,
} from '@/lib/knnResponse';
import { getDistributionPackageMaterial } from '@/lib/packageOptions';
import type { KnnRowSourceType } from '@/lib/knnResponse';

export const buildPalletImageProxyUrl = (azureBlobUrl: string): string | null => {
    if (!azureBlobUrl) return null;

    try {
        const url = new URL(azureBlobUrl);
        const pathParts = url.pathname.split('/');
        if (pathParts.length < 3) return null;

        const containerName = pathParts[1];
        const blobName = decodeURIComponent(pathParts.slice(2).join('/'));
        const proxyUrl = `${backendApiUrl}/media/image/${encodeURIComponent(blobName)}?containerName=${encodeURIComponent(containerName)}`;

        // Keep the query-like container marker inside the encoded path variable.
        // This matches the proven production implementation from commit 3f0b757.
        return proxyUrl.replace(/%2/g, '%252').replace(/\?/g, '%3F').replace(/=/g, '%3D');
    } catch {
        return null;
    }
};

const hasInputValue = (value: unknown) => value !== undefined && value !== null && String(value).trim() !== '';

const NON_MEANINGFUL_KNN_VALUES = new Set(['n/a', 'no data', 'none', '-', 'no tertiary package']);
const hasMeaningfulKnnValue = (value: unknown) => {
    if (!hasInputValue(value)) return false;
    return !NON_MEANINGFUL_KNN_VALUES.has(String(value).trim().toLowerCase());
};

// Add CSS for resizable panels
const panelStyles = {
    panel: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column' as const,
    },
    spiderPanel: {
        minWidth: '300px',
        overflow: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    tablePanel: {
        overflow: 'auto',
    },
    resizeHandle: {
        width: '8px',
        backgroundColor: '#f0f0f0',
        cursor: 'col-resize',
        margin: '0 4px',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s ease',
    },
    resizeBar: {
        width: '2px',
        height: '24px',
        backgroundColor: '#aaa',
        borderRadius: '2px',
    }
};

interface ChooseContentProps {
    requestItem?: any;
    refetch?: () => void;
}

interface TableData {
    optionNo: string;
    variableName?: string;
    rawIndex?: number;
    sourceType?: KnnRowSourceType;
    product: string;
    primary: {
        details: string;
        similarity: number;
    };
    secondary: {
        details: string;
        similarity: number;
    };
    tertiary?: {
        details: string;
        similarity: number | null;
    };
    palletSimilarity?: number | null;
    pallet: {
        pattern: string;
        image: string;
    };
    pallet_pattern: string;
    casesPerLayer?: string;
    layersPerPallet?: string;
    layerPattern: string;
    transit: string;
    stacking: string;
    status?: 'Pass' | 'Fail' | '';
    selected: boolean;
}

const calculateFilledWeight = (netContent, netContentUnit, category) => {


    // If any of the required values are missing, return 0
    if (!netContent || !netContentUnit || !category) return 0;

    // Convert netContent to ml if unit is oz
    const netContentMl = netContentUnit === 'oz' ? netContent * 29.5735 : netContent;

    // Define density values for different categories
    const density: { [key: string]: number } = {
        'CSD': 1.016,
        'SF': 1.027,
        'Coffee Partnership': 1.031,
        'Tea': 1.031,
        'Hydration': 1,
        'juice plus': 1.15
    };

    // Get the density for the given category, defaulting to 1 if not found
    const categoryDensity = density[category] || 1;

    // Calculate the empty bottle weight (you can replace this with actual logic)
    const emptyBottleWeight = 50; // Placeholder, adjust based on your own logic

    // Calculate the filled weight in grams
    const filledWeightGrams = (netContentMl * categoryDensity) + emptyBottleWeight;

    return filledWeightGrams;
};
interface ApiResponse {
    tableData: TableData[];
    graphData: any;
}
const getUnit = (value: string) => {
    if (value === 'inch') {
        return 'in'
    }
    else if (value === 'oz') {
        return 'fl oz'
    } else if (value === 'lbs') {
        return 'lb'
    } else {
        return value
    }
}

const hasDisplayValue = (value: unknown) => {
    const text = String(value ?? '').trim();
    return text !== '' && text !== '0' && text.toLowerCase() !== 'no data' && text.toLowerCase() !== 'n/a';
};

const formatTransitDetails = (shippingMode: unknown, distanceMiles: unknown) => {
    const mode = hasDisplayValue(shippingMode) ? String(shippingMode).trim() : '';
    const distance = hasDisplayValue(distanceMiles) ? `${String(distanceMiles).trim()} Miles` : '';
    return [mode, distance].filter(Boolean).join(' ') || 'No Data';
};

const formatProductSummary = (brand: unknown, volume: unknown, volumeUnit: unknown, packageMaterial: unknown) => {
    return [
        hasDisplayValue(brand) ? String(brand).trim() : '',
        hasDisplayValue(volume) ? `${String(volume).trim()}${hasDisplayValue(volumeUnit) ? ` ${String(volumeUnit).trim()}` : ''}` : '',
        hasDisplayValue(packageMaterial) ? String(packageMaterial).trim() : '',
    ].filter(Boolean).join(', ') || 'No Data';
};

function formatDimensions(dimensionString) {
    // Replace '×' with 'x'
    return dimensionString.replace(/×/g, 'x');
}

// Custom resize handle component
const ResizeHandle = () => {
    return (
        <PanelResizeHandle
            className="w-2 bg-gray-200 hover:bg-gray-300 transition-colors duration-200 cursor-col-resize mx-2 rounded-md flex items-center justify-center"
            style={panelStyles.resizeHandle}
        >
            <div className="w-1 h-10 bg-gray-400 rounded-full" style={panelStyles.resizeBar}></div>
        </PanelResizeHandle>
    );
};

export function ChooseContent({ requestItem, refetch }: ChooseContentProps) {
    const [tableData, setTableData] = useState<TableData[]>([]);
    const [dcsTableData, setDcsTableData] = useState<TableData[]>([]);
    const [graphData, setGraphData] = useState<any>(null);
    const [selectedRow, setSelectedRow] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const { handleNext, setSpiderChartImage } = useStepperContext();
    const [kValue, setKValue] = useState<string>(requestItem?.k_value || '5');
    const [initialSelectedRows, setInitialSelectedRows] = useState<TableData[]>([]);
    const [hasSelectionChanged, setHasSelectionChanged] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [spiderChartWidth, setSpiderChartWidth] = useState(30);
    const [chartSize, setChartSize] = useState({ width: 700, height: 850 });
    const spiderChartRef = useRef<SpiderChartRef>(null);
    const [palletImageSources, setPalletImageSources] = useState<Record<string, string>>({});
    const requestedPalletImages = useRef<Set<string>>(new Set());
    const palletImageBlobUrls = useRef<Set<string>>(new Set());
    const isMounted = useRef(true);

    useEffect(() => {
        setKValue(requestItem?.k_value || '5');
    }, [requestItem?.k_value]);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
            palletImageBlobUrls.current.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
            palletImageBlobUrls.current.clear();
        };
    }, []);
    const optionalKnnFields: Record<string, any> = {};
    const addOptionalKnnField = (key: string, value: any) => {
        if (value !== undefined && value !== null && value !== '') {
            optionalKnnFields[key] = value;
        }
    };

    if (requestItem?.has_tertiary_package === 'yes') {
        addOptionalKnnField('TertiaryPackageType', requestItem?.tertiary_material);
        addOptionalKnnField('TertiaryCaseLength(in)', requestItem?.tertiary_length);
        addOptionalKnnField('TertiaryCaseLength_units', getUnit(requestItem?.tertiary_length_unit));
        addOptionalKnnField('TertiaryCaseWidth(in)', requestItem?.tertiary_width);
        addOptionalKnnField('TertiaryCaseWidth_units', getUnit(requestItem?.tertiary_width_unit));
        addOptionalKnnField('TertiaryCaseHeight(in)', requestItem?.tertiary_height);
        addOptionalKnnField('TertiaryCaseHeight_units', getUnit(requestItem?.tertiary_height_unit));
    }

    addOptionalKnnField('Layers/Pallet', requestItem?.number_of_layers);
    addOptionalKnnField('Cases Per Layer', requestItem?.cases_per_layer);
    addOptionalKnnField('Pallet Pattern Stackup', requestItem?.pallet_pattern);

    let knnDataToSend = {
        data: {
            "SPEC NUMBER": "TEST CASE",
            "Application": requestItem?.application_name,
            "Brand": requestItem?.brand_name,
            "Category": requestItem.category,
            "Package Material": getDistributionPackageMaterial(requestItem.primary_package_type),
            "Volume(ml)": requestItem?.net_contents,
            "VolumeUnits": getUnit(requestItem?.net_contents_unit),
            "Filled Topload": (requestItem?.filled_top_load && (requestItem?.filled_top_load!==undefined || requestItem?.filled_top_load!=="undefined"))?requestItem?.filled_top_load:'',
            "Filled Topload_Units": getUnit(requestItem?.filled_top_load_unit),
            // "Package Material": requestItem?.primary_package_material,
            "Diameter(in)": requestItem?.primary_package_diameter,
            "Diameter_Units": getUnit(requestItem?.primary_package_diameter_unit),
            "Height(in)": requestItem?.primary_package_height,
            "bottleHeight_Units": getUnit(requestItem?.primary_package_height_unit),
            // "FilledWeight(g)": requestItem?.secondary_weight,
            "FilledWeight(g)": calculateFilledWeight(requestItem?.net_contents, requestItem?.net_contents_unit, requestItem?.category).toFixed(2),
            // "FilledWeight_Units": getUnit(requestItem?.secondary_weight_unit),
            "FilledWeight_Units": 'g',
            "Secondary/Tertiary PackageType": requestItem?.secondary_package_type,
            "secondaryBct": (requestItem?.secondary_topload_filled && requestItem?.secondary_topload_filled!==undefined)?requestItem?.secondary_topload_filled:'',
            "secondaryBct_Units": getUnit(requestItem?.secondary_topload_filled_unit),
            "SecondaryMaterial": "Shrink Bundle",
            "CaseLength(in)": requestItem?.secondary_length,
            "CaseLength_units": getUnit(requestItem?.secondary_length_unit),
            "CaseWidth(in)": requestItem?.secondary_width,
            "CaseWidth_Units": getUnit(requestItem?.secondary_width_unit),
            "CaseHeight(in)": requestItem?.secondary_height,
            "CaseHeight_Units": getUnit(requestItem?.secondary_height_unit),
            "CaseWeight": requestItem?.secondary_weight,
            "CaseWeight_Units": getUnit(requestItem?.secondary_weight_unit),
            //have small x instead of multiplication symbol (×)
            "Pallet Material": formatDimensions(requestItem?.pallet_base_type),
            ...optionalKnnFields,
            // "Layers/Pallet": "6",
            // "Pallet Pattern Stackup": "",
            // "Cases Per Layer": "",
            // "Max Pallets Stacked": 2,
            // "Distance(miles)": "1000",
            // "Distance_Unit": "miles",
            // "StackingPattern": "Square",
            // "Shipping Mode": "Road"
        },
        "k": parseInt(requestItem?.k_value || '5')
    }

    const { data: knnData, isLoading: isFetchingKnnData, error: knnError } = useGetKnnDataQuery(
        knnDataToSend.data['Package Material'] === 'Aluminum' ? knnDataToSend : { ...knnDataToSend, data: { ...knnDataToSend.data, "Bottle Design": requestItem?.bottle_design, } },
        { skip: Boolean(requestItem?.isMock) }
    );
    const effectiveKnnData = requestItem?.isMock ? mockKnnData : knnData;
    const hasKnnError = Boolean(knnError && !requestItem?.isMock);
    const [saveChosenCasesMutation, { isLoading: isSaving }] = useSaveChosenCasesMutation();
    const { data: tempUrlsData, isLoading: isFetchingTempUrls } = useGetTempUrlsQuery(tableData.map(row => row.optionNo).join(','), {
        skip: Boolean(requestItem?.isMock) || tableData.length === 0,
    });
    const [selectedRows, setSelectedRows] = useState<TableData[]>([]);
    const [expandedOptions, setExpandedOptions] = useState<Record<string, boolean>>({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<any>(null);

    useEffect(() => {
        if (Array.isArray(effectiveKnnData?.data)) {
            const formattedData = effectiveKnnData?.data.map((row: any[], index: number) => {
                const columns = effectiveKnnData.columns || [];
                const getValue = (columnNames: string[]) => getKnnValue(row, columns, columnNames);
                const getDistanceValue = (columnNames: string[]) => {
                    const distanceRowValue = getKnnValue(effectiveKnnData.data_2?.[index] || [], columns, columnNames);
                    return distanceRowValue !== undefined && distanceRowValue !== null && distanceRowValue !== ''
                        ? distanceRowValue
                        : getValue(columnNames);
                };
                const getDistance = (columnNames: string[]) => {
                    const value = getDistanceValue(columnNames);
                    const numericValue = Number(value);
                    return Number.isFinite(numericValue) ? parseFloat(numericValue.toFixed(2)) : null;
                };
                let sourceType = getKnnRowSourceType(row, columns, row[0]);
                if (requestItem?.isMock && sourceType === 'unknown') {
                    const optionNo = String(row[0] || '');
                    if (optionNo.startsWith('DCS-')) sourceType = 'physical';
                    if (optionNo.startsWith('P')) sourceType = 'specification';
                }
                const rowVariableName = getValue(['Variable Name', 'Pallet Variant', 'Variable', 'Test Variable']) || (sourceType === 'physical' ? 'Physical ship test' : sourceType === 'specification' ? 'Specification match' : 'Unclassified match');
                const isPreSelected = requestItem?.chosenCases?.some(
                    (chosenCase: any) => chosenCase.project_name === row[0] && (!chosenCase.pallet_variant || chosenCase.pallet_variant === rowVariableName)
                );
                const tertiaryType = getValue(['Tertiary PackageType', 'Tertiary Package Type', 'TertiaryMaterial', 'Tertiary Material']);
                const tertiaryLength = getValue(['TertiaryLength(in)', 'Tertiary Length(in)', 'TertiaryLength', 'TertiaryCaseLength(in)', 'Tertiary Case Length(in)', 'Tertiary Case Length']);
                const tertiaryWidth = getValue(['TertiaryWidth(in)', 'Tertiary Width(in)', 'TertiaryWidth', 'TertiaryCaseWidth(in)', 'Tertiary Case Width(in)', 'Tertiary Case Width']);
                const tertiaryHeight = getValue(['TertiaryHeight(in)', 'Tertiary Height(in)', 'TertiaryHeight', 'TertiaryCaseHeight(in)', 'Tertiary Case Height(in)', 'Tertiary Case Height']);
                const tertiarySimilarity = getDistance(['tertiary_distance', 'tertiary_similarity', 'Tertiary Similarity %']);
                const hasTertiaryDetails = [tertiaryType, tertiaryLength, tertiaryWidth, tertiaryHeight].some(hasMeaningfulKnnValue);
                const hasTertiaryMatch = requestItem?.has_tertiary_package === 'yes' && hasTertiaryDetails && tertiarySimilarity !== null;
                const statusValue = String(getValue(OVERALL_DECISION_ALIASES) || '').toLowerCase();
                const brand = getValue(['Brand']);
                const volume = getValue(['Volume(ml)', 'Volume']);
                const volumeUnit = getValue(['VolumeUnits', 'Volume Units']) || (volume ? 'ml' : '');
                const shippingMode = getValue(['Shipping Mode', 'Transit Mode', 'Transit']);
                const distanceMiles = getValue(['Distance(miles)', 'Distance', 'Transit Distance']);
                const category = getValue(['Category']);
                const packageMaterial = getValue(['Package Material', 'Primary Package Material', 'Primary Package Type']);
                const diameter = getValue(['Diameter(in)', 'Diameter', 'Bottle Diameter', 'Bottle Diameter(in)']);
                const height = getValue(['Height(in)', 'Height', 'Bottle Height', 'Bottle Height(in)']);
                const bottleDesign = getValue(['Bottle Design']);
                const secondaryPackageType = getValue(['Secondary/Tertiary PackageType', 'Secondary PackageType', 'Secondary Package Type', 'Package Type']);
                const caseLength = getValue(['CaseLength(in)', 'Case Length(in)', 'Case Length', 'TertiaryCaseLength(in)']);
                const caseWidth = getValue(['CaseWidth(in)', 'Case Width(in)', 'Case Width', 'TertiaryCaseWidth(in)']);
                const caseHeight = getValue(['CaseHeight(in)', 'Case Height(in)', 'Case Height', 'TertiaryCaseHeight(in)']);

                const formattedRow = {
                    optionNo: row[0],
                    variableName: rowVariableName,
                    rawIndex: index,
                    sourceType,
                    product: formatProductSummary(brand, volume, volumeUnit, packageMaterial),
                    primary: {
                        details: requestItem?.primary_package_type === 'pet_bottles' ? `${bottleDesign}, ${category} D:${formatKnnMeasurement(diameter, '')} H:${formatKnnMeasurement(height, '')}` : `${category} D:${formatKnnMeasurement(diameter, '')} H:${formatKnnMeasurement(height, '')}`,
                        similarity: getDistance(['primary_distance', 'primary_similarity', 'Primary Similarity %']) ?? 0
                    },
                    secondary: {
                        details: `${secondaryPackageType} L:${formatKnnMeasurement(caseLength, 'in')} W:${formatKnnMeasurement(caseWidth, 'in')} H:${formatKnnMeasurement(caseHeight, 'in')}`,
                        similarity: getDistance(['secondary_distance', 'secondary_similarity', 'Secondary Similarity %']) ?? 0
                    },
                    tertiary: hasTertiaryMatch
                        ? {
                            details: `${hasMeaningfulKnnValue(tertiaryType) ? tertiaryType : 'Tertiary'}${hasMeaningfulKnnValue(tertiaryLength) ? ` L:${formatKnnMeasurement(tertiaryLength, 'in')}` : ''}${hasMeaningfulKnnValue(tertiaryWidth) ? ` W:${formatKnnMeasurement(tertiaryWidth, 'in')}` : ''}${hasMeaningfulKnnValue(tertiaryHeight) ? ` H:${formatKnnMeasurement(tertiaryHeight, 'in')}` : ''}`,
                            similarity: tertiarySimilarity
                        }
                        : undefined,
                    palletSimilarity: [
                        requestItem?.pallet_pattern,
                        requestItem?.number_of_layers,
                        requestItem?.cases_per_layer,
                    ].some(hasInputValue) ? getDistance(UNITLOAD_SIMILARITY_ALIASES) : null,
                    pallet_pattern: getValue(['Pallet Pattern Stackup', 'Pallet Pattern', 'Pallet Pattern Design']),
                    casesPerLayer: getValue(['Cases Per Layer', 'Cases/Layer']),
                    layersPerPallet: getValue(['Layers/Pallet', 'Layers Per Pallet']),
                    transit: formatTransitDetails(shippingMode, distanceMiles),
                    stacking: getValue(['Stacking Pattern', 'StackingPattern']),
                    status: statusValue.includes('fail') ? 'Fail' : statusValue.includes('pass') ? 'Pass' : '',
                    selected: isPreSelected
                };

                return formattedRow;
            });

            // Initialize selectedRows with pre-selected rows
            const preSelectedRows = formattedData.filter(row => row.selected);
            setSelectedRows(preSelectedRows);

            // Save the initial selection state to compare later
            if (!hasLoaded) {
                setInitialSelectedRows(preSelectedRows);
                setHasLoaded(true);
            }

            // Sort the data by secondary similarity percentage in descending order
            const comparableRows = formattedData.slice(1);
            const sortedData = comparableRows.filter((row) => row.sourceType === 'specification').sort((a, b) =>
                b.secondary.similarity - a.secondary.similarity
            );
            const dcsRows = comparableRows.filter((row) => row.sourceType === 'physical').sort((a, b) =>
                b.secondary.similarity - a.secondary.similarity
            );

            setTableData(sortedData);
            setDcsTableData(dcsRows);

            // Format data for spider chart
            if (formattedData.length > 0) {
                const graphData = {
                    labels: [
                        'Volume',
                        'Filled Weight',
                        'Bottle Diameter',
                        'Bottle Height',
                        'Cases/Layer',
                        'Layers/Pallet',
                    ],
                    currentProject: [100, 100, 100, 100, 100, 100], // Current project is always 100%
                    selectedOption: [
                        formattedData[0].primary.similarity,
                        formattedData[0].secondary.similarity,
                        90, // Example values - replace with actual data
                        85,
                        95,
                        88,
                    ],
                };
                setGraphData(graphData);
            }
        }
    }, [effectiveKnnData, hasLoaded, requestItem]);

    const getRowSelectionKey = (row: TableData) => `${row.rawIndex ?? 'no-index'}-${row.optionNo}-${row.variableName ?? ''}`;

    // Effect to check if selection has changed from initial state
    useEffect(() => {
        if (hasLoaded) {
            // Check if current selection is different from initial selection
            const hasChanged = selectedRows.length !== initialSelectedRows.length ||
                selectedRows.some(current =>
                    !initialSelectedRows.some(initial =>
                        getRowSelectionKey(initial) === getRowSelectionKey(current)
                    )
                ) ||
                initialSelectedRows.some(initial =>
                    !selectedRows.some(current =>
                        getRowSelectionKey(current) === getRowSelectionKey(initial)
                    )
                );

            setHasSelectionChanged(hasChanged);
        }
    }, [selectedRows, initialSelectedRows, hasLoaded]);

    const updateRowSelection = (rows: TableData[], row: TableData, selected: boolean) => rows.map(item =>
        getRowSelectionKey(item) === getRowSelectionKey(row)
            ? { ...item, selected }
            : item
    );

    const handleRowSelect = (row: TableData) => {
        const rowKey = getRowSelectionKey(row);
        if (row.selected) {
            // Deselecting a row
            setSelectedRows(prev => prev.filter(r => getRowSelectionKey(r) !== rowKey));
            setTableData(prev => updateRowSelection(prev, row, false));
            setDcsTableData(prev => updateRowSelection(prev, row, false));
        } else {
            // Selecting a row
            if (selectedRows.length >= 3) {
                showErrorToast("You can only select up to 3 cases.");
                return;
            }
            setSelectedRows(prev => [...prev, row]);
            setTableData(prev => updateRowSelection(prev, row, true));
            setDcsTableData(prev => updateRowSelection(prev, row, true));
        }

        // Update graph data for the selected row
        if (row.selected) {
            setGraphData(prev => ({
                ...prev,
                selectedOption: [
                    row.primary.similarity,
                    row.secondary.similarity,
                    90, // Example values - replace with actual data
                    85,
                    95,
                    88,
                ],
            }));
        }
    };

    const handleSaveSelectedCases = async () => {
        if (selectedRows.length === 0) {
            showErrorToast("Please select at least one case before saving.");
            return;
        }

        // Only save if there have been changes to the selection or it's a new project (no initialSelectedRows)
        if (hasSelectionChanged || initialSelectedRows.length === 0) {
            try {
                console.log(selectedRows.map(row => {
                    const rowIndex = typeof row.rawIndex === 'number'
                        ? row.rawIndex
                        : effectiveKnnData.data.findIndex((dataRow: any[]) => dataRow[0] === row.optionNo);
                    if (rowIndex === -1) return;

                    const rowData = effectiveKnnData.data[rowIndex];
                    return ({
                        project_name: row.optionNo,
                        pallet_variant: row.variableName,
                        request_item_id: requestItem?.id,
                        pallet_pattern: row.pallet_pattern,
                        newVal: getKnnValue(rowData, effectiveKnnData.columns, ['Cases Per Layer', 'Cases/Layer']) || ''
                    })
                }), 'chosen cases array')


                // console.log(rowData[getColumnIndex('Cases Per Layer')] || '','cases per layer')
                const response = await saveChosenCasesMutation(
                    {
                        k_value: kValue,
                        request_item_id: requestItem?.id,
                        cases: selectedRows.map(row => {
                            const rowIndex = typeof row.rawIndex === 'number'
                                ? row.rawIndex
                                : effectiveKnnData.data.findIndex((dataRow: any[]) => dataRow[0] === row.optionNo);
                            const rowData = rowIndex !== -1 ? effectiveKnnData.data[rowIndex] : null;

                            return {
                                project_name: row.optionNo,
                                pallet_variant: row.variableName,
                                request_item_id: requestItem?.id,
                                pallet_pattern: row.pallet_pattern,
                                cases_per_layer: rowData ? getKnnValue(rowData, effectiveKnnData.columns, ['Cases Per Layer', 'Cases/Layer']) || '' : ''
                            };
                        })
                    }
                );

                // Update initial selected rows after saving
                setInitialSelectedRows(selectedRows);
                setHasSelectionChanged(false);

                refetch();
                handleNext();
            } catch (error) {
                console.error('Error saving chosen cases:', error);
                showErrorToast("Failed to save selected cases. Please try again.");
            }
        } else {
            // No changes, just proceed to next step
            handleNext();
        }
    };

    const handleRowClick = (row: TableData) => {
        // Find the full data for this row from knnData
        if (!effectiveKnnData?.data) return;

        const rowIndex = typeof row.rawIndex === 'number'
            ? row.rawIndex
            : effectiveKnnData.data.findIndex((dataRow: any[]) => dataRow[0] === row.optionNo);
        if (rowIndex === -1) return;

        const rowData = effectiveKnnData.data[rowIndex];
        const columns = effectiveKnnData.columns || [];
        const getColumnValue = (columnNames: string[]) => getKnnValue(rowData, columns, columnNames) || '';

        // Format the data for the modal
        const optionData = {
            optionNo: row.optionNo,
            sourceType: row.sourceType,
            product: getColumnValue(['File Name', 'Filename', 'Product File Name']) || row.product,
            palletVariant: getColumnValue(['Pallet Variant', 'Variable Name', 'Variable', 'Test Variable']),
            numberOfPallets: getColumnValue(['Number Of Pallets', 'Number of Pallets']),
            dataSource: getColumnValue(['Data Source', 'Record Source', 'Source']),
            overallDecision: getColumnValue(OVERALL_DECISION_ALIASES),
            brand: getColumnValue(['Brand']),
            category: getColumnValue(['Category']),
            packageMaterial: getColumnValue(['Package Material', 'Primary Package Type']),
            volume: getColumnValue(['Volume(ml)', 'Volume']),
            filledWeight: getColumnValue(['FilledWeight(g)', 'Filled Weight(g)', 'Filled Weight']),
            diameter: getColumnValue(['Diameter(in)', 'Diameter', 'Bottle Diameter', 'Bottle Diameter(in)']),
            height: getColumnValue(['Height(in)', 'Height', 'Bottle Height', 'Bottle Height(in)']),
            bottleDesign: getColumnValue(['Bottle Design']),
            itemsCase: getColumnValue(['Items/Case', 'Items Per Case']),
            caseLength: getColumnValue(['CaseLength(in)', 'Case Length(in)', 'Case Length']),
            caseWidth: getColumnValue(['CaseWidth(in)', 'Case Width(in)', 'Case Width']),
            caseHeight: getColumnValue(['CaseHeight(in)', 'Case Height(in)', 'Case Height']),
            tertiaryPackageType: getColumnValue(['TertiaryPackageType', 'Tertiary Package Type']),
            tertiaryLength: getColumnValue(['TertiaryCaseLength(in)', 'Tertiary Case Length(in)', 'Tertiary Case Length']),
            tertiaryWidth: getColumnValue(['TertiaryCaseWidth(in)', 'Tertiary Case Width(in)', 'Tertiary Case Width']),
            tertiaryHeight: getColumnValue(['TertiaryCaseHeight(in)', 'Tertiary Case Height(in)', 'Tertiary Case Height']),
            palletTypes: getColumnValue(['Pallet Types', 'Pallet Dimension', 'Pallet Dimension (inches)']),
            palletMaterial: getColumnValue(['Pallet Material']),
            unitLoadLength: getColumnValue(['UnitloadLength(in)', 'Unit Load Length(in)', 'Unit Load Length']),
            unitLoadWidth: getColumnValue(['UnitloadWidth(in)', 'Unit Load Width(in)', 'Unit Load Width']),
            unitLoadHeight: getColumnValue(['UnitloadHeight(in)', 'Unit Load Height(in)', 'Unit Load Height']),
            casesPerLayer: getColumnValue(['Cases Per Layer', 'Cases/Layer']),
            layersPerPallet: getColumnValue(['Layers/Pallet', 'Layers Per Pallet']),
            itemsPallet: getColumnValue(['Items/pallet', 'Items/Pallet', 'Items Per Pallet']),
            palletPatternStack: getColumnValue(['Pallet Pattern Stackup', 'Pallet Pattern', 'Pallet Pattern Design']),
            testResults: {
                preShipLean: getColumnValue(['PreShip_Lean', 'Pre Ship Lean']),
                postShipLean: getColumnValue(['PostShip_Lean', 'Post Ship Lean']),
                postShipWalk: getColumnValue(['PostShip_Walk', 'Post Ship Walk']),
                postStackLean: getColumnValue(['PostStack_Lean', 'Post Stack Lean']),
                postStackCompressionRebound: getColumnValue(['Post Stack Compression_Rebound', 'PostStackCompressionRebound', 'Post Stack Compression Rebound']),
            },
            ftl: {
                preShip: {
                    top: getColumnValue(["('PreShipForceToload', 'TOP ')", "('PreShipForceToload', 'TOP')", 'PreShipForceToLoad TOP', 'PreShipForceToload TOP', 'PreShip Force To Load TOP', 'PreShip FTL Top']),
                    middle: getColumnValue(["('PreShipForceToload', 'Middle')", "('PreShipForceToload', 'Middle ')", 'PreShipForceToLoad Middle', 'PreShipForceToload Middle', 'PreShip Force To Load Middle', 'PreShip FTL Middle']),
                    bottom: getColumnValue(["('PreShipForceToload', 'Bottom')", 'PreShipForceToLoad Bottom', 'PreShipForceToload Bottom', 'PreShip Force To Load Bottom', 'PreShip FTL Bottom']),
                },
                postShip: {
                    top: getColumnValue(["('PostShipForceToload', 'TOP ')", "('PostShipForceToload', 'TOP')", 'PostShipForceToLoad TOP', 'PostShipForceToload TOP', 'PostShip Force To Load TOP', 'PostShip FTL Top']),
                    middle: getColumnValue(["('PostShipForceToload', 'Middle ')", "('PostShipForceToload', 'Middle')", 'PostShipForceToLoad Middle', 'PostShipForceToload Middle', 'PostShip Force To Load Middle', 'PostShip FTL Middle']),
                    bottom: getColumnValue(["('PostShipForceToload', 'Bottom')", 'PostShipForceToLoad Bottom', 'PostShipForceToload Bottom', 'PostShip Force To Load Bottom', 'PostShip FTL Bottom']),
                },
            },
            palletImage: tempUrlsData?.find(url => url.folder === row.optionNo && url.filename?.toLowerCase()?.includes('isometric'))?.url || '',
            // You would need to add file attachments here if available
            primaryFiles: tempUrlsData?.filter(url => url.folder === row.optionNo && url.filename?.toLowerCase()?.includes('primary package'))?.map(url => url) || [],
            secondaryFiles: tempUrlsData?.filter(url => url.folder === row.optionNo && url.filename?.toLowerCase()?.includes('secondary package'))?.map(url => url) || [],
            unitFiles: tempUrlsData?.filter(url => url.folder === row.optionNo && url.filename?.toLowerCase()?.includes('unit load'))?.map(url => url) || [],
        };

        setSelectedOption(optionData);
        setIsModalOpen(true);
    };

    // Panel resize handler with aggressive size scaling
    const handlePanelResize = (sizes: number[]) => {
        const panelWidth = sizes[0];
        setSpiderChartWidth(panelWidth);

        const containerWidth = document.querySelector('.space-y-6')?.clientWidth || 1200;
        const panelWidthPixels = (containerWidth * panelWidth) / 100;

        // Aggressively scale chart size with panel width
        // Prioritize chart size over compact layout
        const scaleMultiplier = Math.max(panelWidth / 30, 1); // Start scaling from default size

        // Base sizes
        const baseWidth = 700;
        const baseHeight = 850;

        // Calculate new sizes with more aggressive scaling
        const availableWidth = Math.min(panelWidthPixels * 0.95, 1600); // Allow up to 1600px width
        const widthBasedHeight = availableWidth * 1.1; // Height based on width

        // Use whichever gives a larger chart but don't make it too tall
        const newHeight = Math.min(
            Math.max(baseHeight * scaleMultiplier, widthBasedHeight),
            1400 // Max height cap
        );

        setChartSize({
            width: availableWidth,
            height: newHeight
        });
    };

    // Update window resize handler with the same logic
    useEffect(() => {
        const handleWindowResize = () => {
            if (document.querySelector('.space-y-6')) {
                const containerWidth = document.querySelector('.space-y-6')?.clientWidth || 1200;
                const panelWidthPixels = (containerWidth * spiderChartWidth) / 100;

                // Aggressively scale chart size with panel width
                // Prioritize chart size over compact layout
                const scaleMultiplier = Math.max(spiderChartWidth / 30, 1); // Start scaling from default size

                // Base sizes
                const baseWidth = 700;
                const baseHeight = 850;

                // Calculate new sizes with more aggressive scaling
                const availableWidth = Math.min(panelWidthPixels * 0.95, 1600); // Allow up to 1600px width
                const widthBasedHeight = availableWidth * 1.1; // Height based on width

                // Use whichever gives a larger chart but don't make it too tall
                const newHeight = Math.min(
                    Math.max(baseHeight * scaleMultiplier, widthBasedHeight),
                    1400 // Max height cap
                );

                setChartSize({
                    width: availableWidth,
                    height: newHeight
                });
            }
        };

        window.addEventListener('resize', handleWindowResize);

        // Initial size calculation
        handleWindowResize();

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, [spiderChartWidth]);

    // Method to get chart image for PDF
    const getSpiderChartImage = () => {
        if (spiderChartRef.current) {
            return spiderChartRef.current.getChartImage();
        }
        return null;
    };

    // Capture chart image when data changes
    useEffect(() => {
        if (graphData && spiderChartRef.current) {
            // Small delay to ensure chart is rendered
            setTimeout(() => {
                const chartImage = getSpiderChartImage();
                if (chartImage) {
                    setSpiderChartImage(chartImage);
                }
            }, 1000);
        }
    }, [graphData, setSpiderChartImage]);

    useEffect(() => {
        if (!Array.isArray(tempUrlsData) || tableData.length === 0) return;

        tableData.forEach((row) => {
            const imageData = tempUrlsData.find((url) => (
                url.folder === row.optionNo && url.filename?.toLowerCase()?.includes('isometric')
            ));
            const originalImageUrl = imageData?.url;
            if (!originalImageUrl) return;

            const cacheKey = `${row.optionNo}-${originalImageUrl}`;
            const proxyUrl = buildPalletImageProxyUrl(originalImageUrl);
            if (!proxyUrl || requestedPalletImages.current.has(cacheKey)) return;
            requestedPalletImages.current.add(cacheKey);

            void (async () => {
                try {
                    const oktaUser = await oktaService.getUser();
                    const headers = oktaUser.access_token
                        ? { Authorization: `Bearer ${oktaUser.access_token}` }
                        : {};
                    const response = await fetch(proxyUrl, { headers, credentials: 'include' });
                    if (!response.ok) return;

                    const blobUrl = URL.createObjectURL(await response.blob());
                    if (!isMounted.current) {
                        URL.revokeObjectURL(blobUrl);
                        return;
                    }

                    palletImageBlobUrls.current.add(blobUrl);
                    setPalletImageSources((current) => ({ ...current, [cacheKey]: blobUrl }));
                } catch {
                    // The table continues to use the original signed Azure URL.
                }
            })();
        });
    }, [tempUrlsData, tableData]);

    const hasOptionalUnitLoadInput = [
        requestItem?.pallet_pattern,
        requestItem?.number_of_layers,
        requestItem?.cases_per_layer,
    ].some(hasInputValue);
    const showUnitLoadColumns = hasOptionalUnitLoadInput;
    const showPalletSimilarityColumn = showUnitLoadColumns;

    const renderPalletCell = (row: TableData) => {
        const imageData = Array.isArray(tempUrlsData) && tempUrlsData.find(url => url.folder === row.optionNo && url.filename?.toLowerCase()?.includes('isometric'));
        const originalImageUrl = imageData?.url;

        if (!originalImageUrl) {
            return row.pallet_pattern || 'N/A';
        }

        const cacheKey = `${row.optionNo}-${originalImageUrl}`;
        const imageToUse = palletImageSources[cacheKey] || originalImageUrl;

        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger>
                        <img alt='Pallet Image' width={40} height={40} src={imageToUse} />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="w-[360px] p-0">
                        <Card className="p-4">
                            <img src={imageToUse} alt="Pallet Image" className="object-contain" />
                            <div className='text-lg'>Pallet Pattern - {row.pallet_pattern || 'N/A'}</div>
                        </Card>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    };

    const getSimilarityScore = (row: TableData) => {
        const values = [
            row.primary?.similarity,
            row.secondary?.similarity,
            row.tertiary?.similarity,
            row.palletSimilarity,
        ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

        return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
    };

    type ComparisonTableOptions = {
        showStatus?: boolean;
        selectable?: boolean;
        accordion?: boolean;
        emptyMessage: string;
        variableHeader?: string;
        showVariableColumn?: boolean;
        showPalletColumn?: boolean;
        showTransit?: boolean;
        showTertiaryColumns?: boolean;
    };

    const cellClassName = "px-2 py-3 text-center align-top leading-5 whitespace-normal break-normal [overflow-wrap:normal]";
    const compactCellClassName = "px-2 py-3 text-center align-top leading-5 whitespace-normal break-normal [overflow-wrap:normal]";
    const detailCellClassName = "px-2 py-3 text-left align-top leading-5 whitespace-normal break-normal [overflow-wrap:normal]";
    const wideDetailCellClassName = "px-2 py-3 text-left align-top leading-5 whitespace-normal break-normal [overflow-wrap:normal]";
    const headClassName = "min-w-[88px] font-semibold text-center whitespace-normal break-normal [overflow-wrap:normal] px-2 py-3 leading-5";

    const renderUnitLoadCell = (row: TableData) => {
        const details = [
            row.pallet_pattern,
            row.casesPerLayer ? `Cases/Layer: ${row.casesPerLayer}` : '',
            row.layersPerPallet ? `Layers/Pallet: ${row.layersPerPallet}` : '',
        ].filter(Boolean);

        return details.length ? (
            <div className="space-y-1 text-center">
                {details.map((detail) => (
                    <div key={detail}>{detail}</div>
                ))}
            </div>
        ) : '-';
    };

    const renderDataRow = (
        row: TableData,
        options: ComparisonTableOptions,
        keySuffix = '',
        rowOptions: {
            highlight?: boolean;
            child?: boolean;
            hasChildren?: boolean;
            isExpanded?: boolean;
            onToggle?: () => void;
        } = {}
    ) => (
                        <TableRow
                            key={`${row.optionNo}-${row.rawIndex ?? keySuffix}-${keySuffix}`}
                            className={`${rowOptions.highlight ? 'bg-[#E9F8EF] hover:bg-[#DDF3E7]' : rowOptions.child ? 'bg-[#FBFDFC] hover:bg-[#F3FAF6]' : 'hover:bg-gray-50'}`}
                        >
                            <TableCell className={compactCellClassName}>
                                <div className="flex items-center justify-center gap-1">
                                    {options.accordion && (
                                        !rowOptions.child ? (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                className="h-7 w-7 p-0 text-[#087B61]"
                                                onClick={rowOptions.onToggle}
                                                aria-label={`${rowOptions.isExpanded ? 'Collapse' : 'Expand'} ${row.optionNo}`}
                                            >
                                                {rowOptions.isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                            </Button>
                                        ) : (
                                            <span className="w-3 shrink-0 text-xs font-semibold text-[#087B61]">-&gt;</span>
                                        )
                                    )}
                                    <Button variant="link" className="text-primary p-0 h-auto font-normal" onClick={() => handleRowClick(row)}>
                                        {row.optionNo}
                                    </Button>
                                </div>
                            </TableCell>
                            {options.showVariableColumn !== false && (
                                <TableCell className={`${compactCellClassName} ${rowOptions.child ? 'font-medium text-[#087B61]' : ''}`}>
                                    {row.variableName || '-'}
                                </TableCell>
                            )}
                            <TableCell className={detailCellClassName}>{row.product}</TableCell>
                            <TableCell className={detailCellClassName}>{row.primary.details}</TableCell>
                            <TableCell className={cellClassName}>{row.primary.similarity}%</TableCell>
                            <TableCell className={wideDetailCellClassName}>{row.secondary.details}</TableCell>
                            <TableCell className={cellClassName}>{row.secondary.similarity}%</TableCell>
                            {options.showTertiaryColumns && (
                                <>
                                    <TableCell className={wideDetailCellClassName}>{row.tertiary?.details || '-'}</TableCell>
                                    <TableCell className={cellClassName}>
                                        {row.tertiary?.details === 'No Tertiary package'
                                            ? 'No Tertiary package'
                                            : row.tertiary?.similarity !== null && row.tertiary?.similarity !== undefined ? `${row.tertiary.similarity}%` : '-'}
                                    </TableCell>
                                </>
                            )}
                            {options.showPalletColumn && <TableCell className={cellClassName}>{renderPalletCell(row)}</TableCell>}
                            {showUnitLoadColumns && <TableCell className={cellClassName}>{renderUnitLoadCell(row)}</TableCell>}
                            {showPalletSimilarityColumn && <TableCell className={cellClassName}>{row.palletSimilarity !== null && row.palletSimilarity !== undefined ? `${row.palletSimilarity}%` : '-'}</TableCell>}
                            {options.showTransit && <TableCell className={detailCellClassName}>{row.transit}</TableCell>}
                            {options.showStatus && (
                                <TableCell className={cellClassName}>
                                    {row.status ? (
                                        <span className={`rounded px-2 py-1 text-xs font-semibold text-white ${row.status === 'Pass' ? 'bg-green-600' : 'bg-red-600'}`}>
                                            {row.status}
                                        </span>
                                    ) : '-'}
                                </TableCell>
                            )}
                            {options.selectable && (
                                <TableCell className={cellClassName}>
                                    <Checkbox
                                        checked={row.selected}
                                        onCheckedChange={() => handleRowSelect(row)}
                                        aria-label={`Select option ${row.optionNo}`}
                                    />
                                </TableCell>
                            )}
                        </TableRow>
    );

    const renderComparisonTable = (rows: TableData[], options: ComparisonTableOptions) => {
        const showTertiaryColumns = rows.some(row => (
            Boolean(row.tertiary?.details) &&
            typeof row.tertiary?.similarity === 'number' &&
            Number.isFinite(row.tertiary.similarity)
        ));
        const resolvedOptions = { ...options, showTertiaryColumns };
        const groupedRows = rows.reduce<Record<string, TableData[]>>((acc, row) => {
            const key = row.optionNo || 'N/A';
            acc[key] = acc[key] || [];
            acc[key].push(row);
            return acc;
        }, {});
        const showVariableColumn = options.showVariableColumn !== false;
        const colSpan = 6 + (showVariableColumn ? 1 : 0) + (showTertiaryColumns ? 2 : 0) + (options.showPalletColumn ? 1 : 0) + (showUnitLoadColumns ? 1 : 0) + (showPalletSimilarityColumn ? 1 : 0) + (options.showTransit ? 1 : 0) + (options.showStatus ? 1 : 0) + (options.selectable ? 1 : 0);
        const tableMinWidth = colSpan >= 12 ? 'min-w-[1500px]' : colSpan >= 10 ? 'min-w-[1280px]' : 'min-w-[900px]';

        return (
        <div className="overflow-x-auto">
            <Table className={`${tableMinWidth} table-fixed text-xs xl:text-sm`}>
                <TableHeader>
                    <TableRow className="bg-light-blue">
                        <TableHead className={headClassName}>Option No</TableHead>
                        {showVariableColumn && <TableHead className={headClassName}>{options.variableHeader || 'Variable Name'}</TableHead>}
                        <TableHead className={headClassName}>Product</TableHead>
                        <TableHead className={headClassName}>Primary</TableHead>
                        <TableHead className={headClassName}>Primary Similarity %</TableHead>
                        <TableHead className={headClassName}>Secondary</TableHead>
                        <TableHead className={headClassName}>Secondary Similarity %</TableHead>
                        {showTertiaryColumns && (
                            <>
                                <TableHead className={headClassName}>Tertiary</TableHead>
                                <TableHead className={headClassName}>Tertiary Similarity %</TableHead>
                            </>
                        )}
                        {options.showPalletColumn && <TableHead className={headClassName}>Pallet</TableHead>}
                        {showUnitLoadColumns && <TableHead className={headClassName}>Unitload</TableHead>}
                        {showPalletSimilarityColumn && <TableHead className={headClassName}>Unitload Similarity %</TableHead>}
                        {options.showTransit && <TableHead className={headClassName}>Transit</TableHead>}
                        {options.showStatus && <TableHead className={headClassName}>Status</TableHead>}
                        {options.selectable && <TableHead className={headClassName}>Select</TableHead>}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.length ? Object.entries(groupedRows).flatMap(([optionNo, optionRows]) => {
                        if (options.accordion) {
                            const sortedOptionRows = [...optionRows].sort((a, b) => getSimilarityScore(b) - getSimilarityScore(a));
                            const [topRow, ...childRows] = sortedOptionRows;
                            const isExpanded = Boolean(expandedOptions[optionNo]);

                            return [
                                renderDataRow(topRow, resolvedOptions, 'accordion-top', {
                                    highlight: true,
                                    hasChildren: true,
                                    isExpanded,
                                    onToggle: () => setExpandedOptions((current) => ({ ...current, [optionNo]: !isExpanded })),
                                }),
                                ...(isExpanded
                                    ? childRows.length
                                        ? childRows.map((row, index) => renderDataRow(row, resolvedOptions, `accordion-child-${index}`, { child: true }))
                                        : [
                                            <TableRow key={`${optionNo}-no-more-variants`} className="bg-[#FBFDFC]">
                                                <TableCell colSpan={colSpan} className="px-10 py-3 text-sm text-[#607086]">
                                                    <span className="mr-2 font-semibold text-[#087B61]">-&gt;</span>
                                                    No additional pallet variants for this option.
                                                </TableCell>
                                            </TableRow>
                                        ]
                                    : []),
                            ];
                        }

                        if (optionRows.length === 1) return [renderDataRow(optionRows[0], resolvedOptions, 'single')];

                        const isExpanded = Boolean(expandedOptions[optionNo]);
                        return [
                            <TableRow key={`${optionNo}-group`} className="bg-[#F7FAFE] hover:bg-[#EEF5FF]">
                                <TableCell colSpan={colSpan} className="px-4">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        className="h-auto gap-2 px-0 py-1 text-blue"
                                        onClick={() => setExpandedOptions((current) => ({ ...current, [optionNo]: !isExpanded }))}
                                    >
                                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                        <span className="font-semibold">{optionNo}</span>
                                        <span className="text-xs text-[#52657A]">({optionRows.length} records)</span>
                                    </Button>
                                </TableCell>
                            </TableRow>,
                            ...(isExpanded ? optionRows.map((row, index) => renderDataRow(row, resolvedOptions, `expanded-${index}`)) : []),
                        ];
                    }) : (
                        <TableRow>
                            <TableCell colSpan={colSpan} className="h-24 text-center text-gray-500">
                                {options.emptyMessage}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
        );
    };

    if ((isFetchingKnnData && !requestItem?.isMock) || isFetchingTempUrls) {
        return <div className='flex justify-center items-center h-[50vh]'>
            <Loader2 className='w-10 h-10 animate-spin' />
        </div>
    }
    if (hasKnnError) {
        return (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                Unable to load similar package recommendations. Please retry after the Python service is available.
            </div>
        );
    }
    return (
        <div className="space-y-4">
            <PanelGroup direction="horizontal" onLayout={handlePanelResize}>
                <Panel defaultSize={38} minSize={30} style={{ ...panelStyles.panel, ...panelStyles.spiderPanel }}>
                    <div className="border rounded-lg h-full w-full overflow-auto p-1">
                        {graphData && <SpiderChart
                            ref={spiderChartRef}
                            itemPerCaseForTestCast={requestItem?.items_per_case?.split(' ')[0]}
                            controls={true}
                            data={effectiveKnnData}
                            chartWidth={chartSize.width}
                            chartHeight={chartSize.height}
                        />}
                    </div>
                </Panel>

                <ResizeHandle />

                <Panel defaultSize={62} minSize={40} style={{ ...panelStyles.panel, ...panelStyles.tablePanel }}>
                    <div className="flex h-full flex-col gap-6 overflow-auto">
                        <div className="rounded-lg border bg-white">
                            <div className="border-b bg-[#F7FAFE] px-4 py-2">
                                <h3 className="text-base font-semibold text-blue">Specifications</h3>
                                <p className="text-xs text-[#52657A]">
                                    Package specifications similar to the current package.
                                </p>
                            </div>
                            {renderComparisonTable(tableData, {
                                selectable: true,
                                showVariableColumn: false,
                                showPalletColumn: true,
                                emptyMessage: 'No matching configuration'
                            })}
                        </div>
                        <div className="rounded-lg border bg-white">
                            <div className="border-b bg-[#F7FAFE] px-4 py-2">
                                <h3 className="text-base font-semibold text-blue">Physical Ship Tests</h3>
                                <p className="text-xs text-[#52657A]">
                                    Similar physical ship test records from prior package test data.
                                </p>
                            </div>
                            {renderComparisonTable(dcsTableData, {
                                showStatus: true,
                                showTransit: true,
                                accordion: true,
                                selectable: true,
                                variableHeader: 'Pallet Variant',
                                emptyMessage: 'No matching configuration'
                            })}
                        </div>
                    </div>
                </Panel>
            </PanelGroup>
            <div className="flex justify-end">
                <button
                    id="save-selected-cases-button"
                    style={{ display: 'none' }}
                    onClick={handleSaveSelectedCases}
                    disabled={selectedRows.length === 0 || isSaving}
                    className={`px-4 py-2 rounded ${selectedRows.length === 0 || isSaving
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-blue text-white hover:bg-blue/90'
                        }`}
                    title="Save selected cases"
                >
                    {isSaving ? 'Saving...' : 'Save Selected Cases'}
                </button>
            </div>
            {selectedOption && (
                <OptionDetailsModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    optionData={selectedOption}
                />
            )}
        </div>
    );
}
